import { HandleErrorAndLoading } from "@common/handleErrorAndLoading/HandleErrorAndLoading";
import { Switch, Typography } from "@material-ui/core";
import Card from "@material-ui/core/Card";
import CardContent from "@material-ui/core/CardContent";
import Grid from "@material-ui/core/Grid";
import { useBarringList } from "./hook/useBarringList";
import CheckCircleOutlineIcon from "@material-ui/icons/CheckCircleOutline";
import { useTranslation } from "react-i18next";
import { useContext, useEffect, useState } from "react";
import { Loader } from "@common/handleErrorAndLoading/Loader";
import { SelectedServiceContext } from "@context/ISelectedContext";
import { Modal } from "@common/Modal/Modal";

interface IBarringState {
    isModalOpen: boolean;
    barringCode: string;
    status: boolean;
    setIsChecked: (checked: boolean) => void;
    toggleEvent: (code: string, isChecked: boolean, setCheckedState: (val: boolean) => void) => void;
}

interface IParams {
    barringCode: string;
    isActive: boolean;
    setModalState: (value: IBarringState) => void;
}

const defaultModalState: IBarringState = {
    isModalOpen: false,
    barringCode: "",
    status: false,
    setIsChecked: () => {},
    toggleEvent: () => {},
};

const BarringToggle = (props: IParams) => {
    const { barringCode, isActive, setModalState } = props;
    const [isChecked, setIsChecked] = useState<boolean>(false);
    const { postBarring, postBarringState } = useBarringList();

    const handleToggleEvent = (code: string, isChecked: boolean, setCheckedState: (val: boolean) => void) => {
        postBarring(code, isChecked ? "ACTIVATION" : "TERMINATION", isChecked, setCheckedState);
    };

    const handleToggleChange = (status: boolean) => {
        setModalState({
            barringCode: barringCode,
            status: status,
            setIsChecked: setIsChecked,
            isModalOpen: true,
            toggleEvent: handleToggleEvent,
        });
    };

    useEffect(() => {
        setIsChecked(isActive);
    }, [isActive]);

    return postBarringState.isLoading ? (
        <Loader size={30} />
    ) : (
        <Switch
            checked={isChecked}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                handleToggleChange(event.target.checked);
            }}
            id={barringCode}
        />
    );
};

export const BarringList = () => {
    const { selectedService } = useContext(SelectedServiceContext);
    const {
        getBarringListState: { error, isLoading },
        barringList,
        getBarringList,
    } = useBarringList();
    const { t } = useTranslation("barrings");
    const [modalState, setModalState] = useState<IBarringState>(defaultModalState);

    useEffect(() => {
        getBarringList();
    }, [selectedService]);

    const handleActionConfirmation = (
        barringCode: string,
        status: boolean,
        setIsChecked: (checked: boolean) => void
    ) => {
        modalState.toggleEvent(barringCode, status, setIsChecked);
        setModalState(defaultModalState);
    };

    return (
        <Card elevation={2}>
            <CardContent>
                <HandleErrorAndLoading error={error} isLoading={isLoading} retryFetch={getBarringList}>
                    <Grid item container direction="row" justify="space-around" alignItems="center" spacing={2} xs={12}>
                        <Grid item xs={6}></Grid>
                        <Grid item container xs={3} justify="center">
                            <Typography align="center">{t("appliedByCollection")}</Typography>
                        </Grid>
                        <Grid item container xs={3} justify="center">
                            <Typography align="center">{t("status")}</Typography>
                        </Grid>
                    </Grid>
                    {barringList.map(({ addedByCollection, name, barringCode, isActive }) => (
                        <Grid
                            item
                            container
                            key={barringCode}
                            xs={12}
                            direction="row"
                            justify="space-around"
                            alignItems="center"
                            spacing={2}
                        >
                            <Grid item xs={6}>
                                <Typography>{name}</Typography>
                            </Grid>
                            <Grid item container xs={3} justify="center">
                                {addedByCollection && <CheckCircleOutlineIcon style={{ color: "green" }} />}
                            </Grid>
                            <Grid item container xs={3} justify="center">
                                {!addedByCollection && (
                                    <BarringToggle
                                        barringCode={barringCode}
                                        isActive={isActive}
                                        setModalState={setModalState}
                                    />
                                )}
                            </Grid>
                        </Grid>
                    ))}
                </HandleErrorAndLoading>
                <Modal
                    close={() =>
                        setModalState((prevState: IBarringState) => {
                            return { ...prevState, isModalOpen: false };
                        })
                    }
                    confirm={() =>
                        handleActionConfirmation(modalState.barringCode, modalState.status, modalState.setIsChecked)
                    }
                    content={t("toggleConfirmationMessage")}
                    displayCancel
                    isModalOpen={modalState.isModalOpen}
                    title={t("barMobileServices")}
                />
            </CardContent>
        </Card>
    );
};
